import classNames from "classnames"
import {kebabCase, startCase} from "lodash"
import React from "react"
import {useNavigate, useParams} from "react-router-dom"
import {BillComponent, BillLineItemsCard} from "../../components/Bills/Bill"
import {BillEventsTable} from "../../components/Bills/BillEventsTable"
import {TablePending} from "../../components/DataTable/DataTable"
import Icon from "../../components/Icon/Icon"
import TitleBar, {MetaItemProps} from "../../components/TitleBar/TitleBar"
import {AsyncResultComponent} from "../../containers/AsyncResult/AsyncResult"
import MainSectionLayout from "../../containers/MainSection/MainSectionLayout"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {useListReturnPath} from "../../hooks/useListReturnPath"
import {useRefreshToken} from "../../hooks/useRefreshToken"
import {
	BillDeductionStartedEventType,
	BillDetailInclude,
	BillFundsPushedToVendorEventType,
	findBillEventPaymentEntityId,
	findBillEventPaymentId,
	findBillFile,
	findVendor,
	getBill,
	getBillEvents,
	getBillVendorPaymentPath,
	getVendorName,
} from "../../resources/bills"
import {Customer, CustomerResourceTypes} from "../../resources/customer"
import {Org, orgType} from "../../resources/org"
import {useAccessToken, useIsUnitAdminUser} from "../../services/auth"

export default function BillPage() {
	const accessToken = useAccessToken()
	const navigate = useNavigate()
	const isUnitAdmin = useIsUnitAdminUser()
	const {billId = ""} = useParams<{billId: string}>()
	const listReturnPath = useListReturnPath("/bills/")
	const [refreshToken] = useRefreshToken()
	const billAsyncResultReq = useAsyncResult(
		() => getBill(accessToken, billId, BillDetailInclude),
		[refreshToken, billId]
	)
	const billEventsAsyncResultReq = useAsyncResult(() => getBillEvents(accessToken, billId), [refreshToken, billId])

	return (
		<div className="bill-full-page">
			<AsyncResultComponent asyncResult={billAsyncResultReq}>
				{({value: billResponse}) => {
					const bill = billResponse.data
					const included = billResponse.included ?? []
					const customerId = bill.relationships.customer?.data.id
					const vendorId = bill.relationships.vendor?.data.id
					const customer = included.find(
						(r) => CustomerResourceTypes.includes(r.type) && r.id === customerId?.toString()
					) as Customer | undefined
					const vendor = vendorId ? findVendor(vendorId, included) : undefined
					const org = included.find((r) => r.type === orgType) as Org | undefined
					const billFile = findBillFile(bill, included)
					const vendorName = vendor ? getVendorName(vendor) : vendorId
					const billEvents = billEventsAsyncResultReq.isOk() ? billEventsAsyncResultReq.value.data : []
					const deductionPaymentId = findBillEventPaymentId(billEvents, BillDeductionStartedEventType)
					const vendorPaymentId = findBillEventPaymentEntityId(billEvents, BillFundsPushedToVendorEventType)
					const vendorPaymentPath = vendorPaymentId
						? getBillVendorPaymentPath(bill.attributes.paymentMethod, vendorPaymentId)
						: undefined

					const meta: MetaItemProps[] = [
						...(org
							? [
									{
										text: org.attributes.name,
										icon: "man-business-suitcase---users",
										path: `/orgs/${org.id}`,
									} as MetaItemProps,
							  ]
							: []),
						{
							text: "Customer",
							icon: "user-geometric-action-money-fund---users",
							path: `/customer/${customerId}`,
						},
						...(vendorName
							? [
									{
										text: vendorName,
										icon: "billing-account--business-products",
										...(vendorId ? {path: `/vendors/${vendorId}`} : {}),
									} as MetaItemProps,
							  ]
							: []),
						...(deductionPaymentId
							? [
									{
										text: "Deduction Payment",
										icon: "money-transfer--business-products",
										path: `/payments/${deductionPaymentId}`,
									} as MetaItemProps,
							  ]
							: []),
						...(vendorPaymentPath
							? [
									{
										text: "Vendor Payment",
										icon: "money-transfer--business-products",
										path: vendorPaymentPath,
									} as MetaItemProps,
							  ]
							: []),
					]

					return (
						<>
							<TitleBar
								title={`Bill #${billId}`}
								breadcrumbs={[{text: "Bills", path: listReturnPath}]}
								status={{
									text: startCase(bill.attributes.status),
									className: classNames("bills-status", kebabCase(bill.attributes.status)),
								}}
								meta={meta}
							>
								{isUnitAdmin && (
									<button
										className="button page-title-button"
										type="button"
										onClick={() => navigate(`/bills/${billId}/edit`)}
									>
										<Icon icon={"pencil-1--interface-essential"} size={16} />
										<span>Edit</span>
									</button>
								)}
							</TitleBar>
							<MainSectionLayout fullWidth={true}>
								<div className="columns">
									<div className="column is-4">
										<BillComponent
											billId={billId}
											accessToken={accessToken}
											useDrawer={false}
											customer={customer}
											vendor={vendor}
											billFile={billFile}
											billAsyncResult={billAsyncResultReq}
											hideLineItems={true}
										/>
									</div>
									<div className="column">
										<AsyncResultComponent
											asyncResult={billEventsAsyncResultReq}
											pendingComponent={<TablePending numberOfRows={6} />}
										>
											{({value: billEventsResponse}) => <BillEventsTable events={billEventsResponse.data} />}
										</AsyncResultComponent>
										<div style={{marginTop: "1.5rem"}}>
											<BillLineItemsCard bill={bill} />
										</div>
									</div>
								</div>
							</MainSectionLayout>
						</>
					)
				}}
			</AsyncResultComponent>
		</div>
	)
}
